"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

const WA_MESSAGE = encodeURIComponent(
  "Hi, I'm interested in Scaled-Out infrastructure. My monthly ad spend is approximately $____. I operate in ______ market. Looking to scale without spending limits."
);
const WA_URL = `https://wa.me/447700000000?text=${WA_MESSAGE}`;

export default function PricingCards() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-24 bg-[#0B0F14]"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Standard Tier */}
          <div className="pricing-card-featured rounded-2xl p-10 bg-[#11161D] reveal">
            <div className="tag-badge mb-4">Standard Tier</div>
            <div className="flex items-end gap-2 mb-1 mt-6">
              <span
                className="font-mono font-bold text-[#00E676]"
                style={{
                  fontSize: "5rem",
                  lineHeight: 1,
                  textShadow: "0 0 40px rgba(0,230,118,0.4)",
                }}
              >
                4%
              </span>
            </div>
            <p className="text-[#6B7A8D] text-sm font-mono mb-8">
              per top-up · no other fees
            </p>

            <div className="divider-glow mb-8" />

            <div className="space-y-4 mb-10">
              {[
                { label: "Monthly Fee", val: "£0", positive: true },
                { label: "Setup Costs", val: "£0", positive: true },
                { label: "Lock-In Contracts", val: "None", positive: true },
                { label: "Spending Limits", val: "None", positive: true },
                { label: "Top-Up Speed", val: "<15 min avg", positive: true },
                { label: "Deployment Time", val: "1 Business Day", positive: true },
                { label: "Support", val: "24/7", positive: true },
              ]?.map((row) => (
                <div key={row?.label} className="flex justify-between items-center">
                  <span className="text-[#6B7A8D] text-sm">{row?.label}</span>
                  <span
                    className={`font-mono text-sm font-bold ${
                      row?.positive ? "text-[#00E676]" : "text-[#6B7A8D]"
                    }`}
                  >
                    {row?.val}
                  </span>
                </div>
              ))}
            </div>

            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-solid-glow w-full py-4 rounded-xl text-sm font-bold uppercase tracking-[0.1em] block text-center"
            >
              Start On WhatsApp →
            </a>
            <p className="text-[#6B7A8D] text-[11px] font-mono text-center mt-3 tracking-[0.05em]">
              Onboarding review required before activation.
            </p>
          </div>

          {/* Agency Tier */}
          <div className="card-neon rounded-2xl p-10 bg-[#11161D] reveal reveal-delay-2">
            <div className="tag-badge mb-4">Agency Tier</div>
            <div className="flex items-end gap-2 mb-1 mt-6">
              <span
                className="font-mono font-bold text-[#F0F4F8]"
                style={{ fontSize: "3.5rem", lineHeight: 1 }}
              >
                Custom
              </span>
            </div>
            <p className="text-[#6B7A8D] text-sm font-mono mb-8">
              rate based on volume & KPIs
            </p>

            <div className="divider-glow mb-8" />

            <div className="space-y-4 mb-10">
              {[
                { label: "Rate Structure", val: "Volume-Based" },
                { label: "Account Manager", val: "Dedicated" },
                { label: "Support Line", val: "Private" },
                { label: "Scaled-Out Branding", val: "Zero" },
                { label: "KPI Alignment", val: "Full" },
                { label: "Capital Scaling", val: "Flexible" },
                { label: "Onboarding", val: "Priority" },
              ]?.map((row) => (
                <div key={row?.label} className="flex justify-between items-center">
                  <span className="text-[#6B7A8D] text-sm">{row?.label}</span>
                  <span className="text-[#F0F4F8] font-mono text-sm font-bold">
                    {row?.val}
                  </span>
                </div>
              ))}
            </div>

            <Link
              href="/agency"
              className="btn-glow w-full py-4 rounded-xl text-sm font-bold uppercase tracking-[0.1em] block text-center"
            >
              Apply For Agency Tier →
            </Link>
            <p className="text-[#6B7A8D] text-[11px] font-mono text-center mt-3 tracking-[0.05em]">
              Minimum volume thresholds apply.
            </p>
          </div>
        </div>

        {/* Bottom Clarification */}
        <div className="mt-12 grid md:grid-cols-3 gap-4 reveal reveal-delay-3">
          {[
            { title: "No Hidden Costs", desc: "The 4% rate is the complete fee structure. No onboarding charges, no monthly minimums." },
            { title: "Pay As You Scale", desc: "Fees are only incurred when you deploy capital. Zero cost when inactive." },
            { title: "Volume Rewards", desc: "Agency tier rates decrease as monthly volume increases. Growth is incentivised." },
          ]?.map((item) => (
            <div key={item?.title} className="card-neon rounded-xl p-6">
              <h4 className="text-[#F0F4F8] font-bold text-sm mb-2">{item?.title}</h4>
              <p className="text-[#6B7A8D] text-xs leading-relaxed">{item?.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}